import type { StationSource } from './stations';

/**
 * Katalog postaj — oblika zadetka iz spletne baze in njegovo branje (T3 §1).
 *
 * Ta datoteka ne pozna ne omrežja ne DOM-a. Razlog je isti kot pri
 * `domain/library.ts`: zadetek iz kataloga gre lahko naravnost v uporabnikov
 * seznam, in preslikava, ki bi se dala preveriti šele z živim strežnikom, bi
 * se preverjala takrat, kadar je radio-browser dosegljiv — torej ne vedno.
 *
 * ⚠️ **Katalog ni shramba.** `CatalogStation` je zadetek, ne postaja: nima
 * našega `id`, nima `order` in ne ve, ali je v seznamu. Šele `toStationInput()`
 * naredi iz njega nekaj, kar zna knjižnica sprejeti.
 */

export type CatalogSource = Extract<StationSource, 'radio-browser' | 'webradiodb'>;

export interface CatalogStation {
  /** `stationuuid` vira. NI naš `id` — ta nastane šele ob dodajanju (T2 §2). */
  uuid: string;
  name: string;
  url: string;
  /** Naslov, kot ga je vir že razrešil; pogosto se od `url` razlikuje. */
  urlResolved?: string;
  homepage?: string;
  favicon?: string;
  country?: string;
  countryCode?: string;
  tags: string[];
  codec?: string;
  /** kbps; 0 ali manjka = neznano. Kakovost NI status (CLAUDE.md). */
  bitrate?: number;
  isHls: boolean;
  /**
   * Ali postaja oddaja prek `https:`.
   *
   * 🔴 **Izračunano tu in prikazano v seznamu, ne šele ob predvajanju** (P2 §2,
   * opomba): 42 % kataloga je samo HTTP in brskalnik tak tok s HTTPS strani
   * blokira. Uporabnik mora to vedeti, preden postajo doda, ne potem.
   */
  isHttps: boolean;
  /** Kliki skupnosti — vir razvrstitve po priljubljenosti (P2 §2.3). */
  clickCount: number;
  votes: number;
  source: CatalogSource;
}

/* ── Branje odgovora vira ──────────────────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

function num(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // radio-browser pošlje `bitrate` kot število, WebRadioDB ponekod kot niz.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * Žanri iz vira.
 *
 * radio-browser jih pošlje kot en niz z vejicami, WebRadioDB kot polje.
 * Male črke so zahteva shrambe (T2 §2) in hkrati pogoj, da prevod v
 * `formatGenre()` sploh najde ključ.
 */
export function parseTagList(raw: unknown): string[] {
  const parts = Array.isArray(raw)
    ? raw.filter((t): t is string => typeof t === 'string')
    : typeof raw === 'string'
      ? raw.split(',')
      : [];

  const seen = new Set<string>();
  for (const part of parts) {
    const tag = part.trim().toLowerCase();
    if (tag !== '') seen.add(tag);
  }
  return [...seen];
}

/**
 * Zadetek iz kataloga, z zavrnitvijo vsega, kar ni predvajljivo.
 *
 * `name` in `url` sta obvezna — zadetek brez njiju ni okrnjen, ampak
 * nesmiseln. `uuid` je obvezen zato, ker brez njega ni mogoče javiti klika
 * nazaj (P2 §2.8) in ni stabilnega ključa za vrstico seznama.
 *
 * ⚠️ **`hidebroken=true` ne pomeni, da je vsak zadetek uporaben.** Vir vrača
 * tudi vnose s praznim naslovom; v `dir.xiph.org` je takih 59 % (T3 §1). Ena
 * postaja manj v seznamu je boljša od vrstice, ki ob dotiku ne naredi nič.
 */
export function parseCatalogStation(raw: unknown, source: CatalogSource): CatalogStation | null {
  if (!isRecord(raw)) return null;

  const uuid = str(raw.stationuuid) ?? str(raw.uuid) ?? str(raw.id);
  const name = str(raw.name);
  const url = str(raw.url) ?? str(raw.stream);
  if (uuid === undefined || name === undefined || url === undefined) return null;
  if (!/^https?:\/\//i.test(url)) return null;

  // Vir naslov pogosto že razreši; kadar se ujemata, drugega zapisa ni treba.
  const resolvedRaw = str(raw.url_resolved) ?? str(raw.urlResolved);
  const resolved = resolvedRaw === url ? undefined : resolvedRaw;

  const country = str(raw.country);
  const countryCode = str(raw.countrycode) ?? str(raw.countryCode);
  const homepage = str(raw.homepage);
  const favicon = str(raw.favicon);
  const codec = str(raw.codec);
  const bitrate = num(raw.bitrate);

  const station: CatalogStation = {
    uuid,
    name,
    url,
    tags: parseTagList(raw.tags),
    // `hls` je pri radio-browserju 0/1, ne boolean. `isHls` je naša oblika:
    // odgovor Workerja gre skozi isto branje še enkrat, ko ga prebere odjemalec.
    isHls: raw.hls === 1 || raw.hls === true || raw.hls === '1' || raw.isHls === true,
    isHttps: (resolved ?? url).toLowerCase().startsWith('https:'),
    clickCount: num(raw.clickcount) ?? num(raw.clickCount) ?? 0,
    votes: num(raw.votes) ?? 0,
    source,
  };

  if (resolved !== undefined) station.urlResolved = resolved;
  if (homepage !== undefined) station.homepage = homepage;
  if (favicon !== undefined) station.favicon = favicon;
  if (country !== undefined) station.country = country;
  if (countryCode !== undefined) station.countryCode = countryCode.toUpperCase();
  if (codec !== undefined) station.codec = codec.toLowerCase();
  // 0 kbps je »neznano«, ne »nič« — zapis bi v vrstici izpisal „0 kbps".
  if (bitrate !== undefined && bitrate > 0) station.bitrate = bitrate;

  return station;
}

/** Zadetki iz odgovora vira; neveljavni tiho odpadejo, ostali preživijo. */
export function parseCatalogStations(raw: unknown, source: CatalogSource): CatalogStation[] {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const stations: CatalogStation[] = [];
  for (const item of raw) {
    const station = parseCatalogStation(item, source);
    if (station === null || seen.has(station.uuid)) continue;
    seen.add(station.uuid);
    stations.push(station);
  }
  return stations;
}

/* ── Filtri ────────────────────────────────────────────────────────── */

/** Ena izbira v comboboxu države ali žanra (P2 §2.2). */
export interface CatalogFacet {
  /** Koda države (`SI`) ali ime žanra (`jazz`) — kar gre v poizvedbo. */
  value: string;
  /** Koliko postaj vir pod to izbiro pozna. Podatek, ne status. */
  count: number;
}

export function parseFacets(raw: unknown, valueKey: string): CatalogFacet[] {
  if (!Array.isArray(raw)) return [];

  const facets: CatalogFacet[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const value = str(item[valueKey]) ?? str(item.name);
    /**
     * ⚠️ Dve imeni istega podatka, obe pravi.
     *
     * `stationcount` je oblika vira, `count` pa naša — odgovor Workerja gre
     * skozi to branje **še enkrat**, ko ga prebere odjemalec. Brez druge veje
     * je vsak facet padel na 0 in oba spustna seznama sta ostala prazna, brez
     * ene same napake. Ujel ga je test, ne pregled kode.
     */
    const count = num(item.stationcount) ?? num(item.stationCount) ?? num(item.count) ?? 0;
    // Vir pošilja tudi prazne kode in žanre z eno samo postajo v napačnem
    // zapisu. Prazna izbira v comboboxu je vrstica, ki ne pomeni ničesar.
    if (value === undefined || count <= 0) continue;
    facets.push({ value, count });
  }
  return facets;
}

/**
 * ⚠️ **Preslikave v `Station` tu NI, in to ni pomota.**
 *
 * `fromCatalog()` in `findCatalogMatch()` stojita v `domain/library.ts`, ker
 * ta datoteka nima **nobene** izvajalne odvisnosti — uvaža le tip. To je
 * pogoj, da jo sme uvoziti Cloudflare Worker (T4 §1): veriga do knjižnice
 * pripelje do `i18n`, ta pa do `localStorage`, ki ga v okolju Workerja ni.
 *
 * Podvojena izvedba branja bi bila slabša izbira. Enkrat se je to že zgodilo
 * pri ključu ovitkov in razšla sta se za en vezaj — brez vidne napake, le s
 * tiho razpolovljenim predpomnilnikom (glej `src/domain/artwork.ts`).
 */
